import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext.jsx';
import { Box, Typography, Card, Grid, Chip, Avatar } from '@mui/joy';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { supabase } from '../../supabaseClient';
import LoadingSkeleton, { CardSkeleton } from '../../components/LoadingSkeleton';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, ChartDataLabels);

const ClientAnalytics = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [clientIds, setClientIds] = useState([]);
  const [projects, setProjects] = useState([]);
  const [permits, setPermits] = useState([]);

  useEffect(() => {
    if (!user?.id) return;

    const fetchUserClients = async () => {
      const { data, error } = await supabase
        .from('user_clients')
        .select('client_id')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching user_clients:', error);
        return;
      }

      const ids = data.map(row => row.client_id);
      setClientIds(ids);
    };

    fetchUserClients();
  }, [user]);

  useEffect(() => {
    if (!clientIds.length) return;

    const fetchProjectsAndPermits = async () => {
      let projectQuery = supabase.from('projects').select('id').in('client_id', clientIds);

      const { data: projectData, error: projectError } = await projectQuery;

      if (projectError) {
        console.error('Error fetching projects:', projectError);
        return;
      }

      console.log('Projects fetched:', projectData);

      const projectIds = projectData.map(p => p.id);
      console.log('Number of projects found:', projectData.length);

      const { data: permitData, error: permitError } = await supabase
        .from('project_records')
        .select('*')
        .in('project_id', projectIds)
        .in('record_type', ['Permit', 'Plans', 'Bond'])
        .eq('archived', false);

      if (permitError) {
        console.error('Error fetching permits:', permitError);
        return;
      }

      console.log('Permits fetched:', permitData);
      console.log('Number of permit records found:', permitData.length);

      setPermits(permitData || []);
      setLoading(false);
    };

    fetchProjectsAndPermits();
  }, [clientIds]);


  // Helper function for status colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'In Progress': return '#00A884';
      case 'In Review': return '#ffc107';
      case 'Active': return '#28a745';
      case 'Certified': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'In Progress': return 'hourglass_empty';
      case 'In Review': return 'rate_review';
      case 'Active': return 'check_circle';
      case 'Certified': return 'verified';
      default: return 'help';
    }
  };

  if (loading) {
    return (
      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid',
          borderColor: 'success.200',
          boxShadow: '0 4px 20px rgba(0, 168, 132, 0.08)',
          borderRadius: 'xl',
          overflow: 'hidden',
          p: 0
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100' }}>
          <LoadingSkeleton height="32px" width="200px" />
        </Box>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid xs={12} md={8}>
              <CardSkeleton height="350px" />
            </Grid>
            <Grid xs={12} md={4}>
              <CardSkeleton height="350px" />
            </Grid>
          </Grid>
        </Box>
      </Card>
    );
  }

  if (!clientIds.length) {
    return (
      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid',
          borderColor: 'success.200',
          boxShadow: '0 4px 20px rgba(0, 168, 132, 0.08)',
          borderRadius: 'xl',
          p: 4,
          textAlign: 'center'
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'warning.100',
            color: 'warning.600',
            width: 64,
            height: 64,
            mx: 'auto',
            mb: 2,
            fontSize: '2rem'
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '32px', fontFamily: 'Material Symbols Outlined' }}>
            warning
          </span>
        </Avatar>
        <Typography level="h5" sx={{ color: 'warning.600', mb: 1 }}>No Client Access</Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          No client is currently associated with your account. Please contact your administrator.
        </Typography>
      </Card>
    );
  }

  if (!permits.length) {
    return (
      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid',
          borderColor: 'success.200',
          boxShadow: '0 4px 20px rgba(0, 168, 132, 0.08)',
          borderRadius: 'xl',
          p: 4,
          textAlign: 'center'
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'neutral.100',
            color: 'neutral.600',
            width: 64,
            height: 64,
            mx: 'auto',
            mb: 2,
            fontSize: '2rem'
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '32px', fontFamily: 'Material Symbols Outlined' }}>
            assignment
          </span>
        </Avatar>
        <Typography level="h5" sx={{ color: 'neutral.600', mb: 1 }}>No Records Found</Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          No permit records found for your assigned projects. New permits will appear here when available.
        </Typography>
      </Card>
    );
  }

  const statusCounts = { 'In Progress': 0, 'In Review': 0, 'Active': 0, 'Certified': 0 };
  const agencyCounts = {};

  permits.forEach(permit => {
    const { start_date, application_date, issue_date, expiration_date, agency } = permit;

    if (start_date && application_date && !issue_date && !expiration_date) {
      statusCounts['In Review']++;
    } else if (start_date && !application_date && !issue_date && !expiration_date) {
      statusCounts['In Progress']++;
    } else if (issue_date && expiration_date) {
      statusCounts['Active']++;
    } else if (expiration_date && !issue_date) {
      statusCounts['Active']++;
    }

    if (permit.certified) {
      statusCounts['Certified']++;
    }

    if (agency) {
      agencyCounts[agency] = (agencyCounts[agency] || 0) + 1;
    }
  });

  const statusChartData = {
    labels: Object.keys(statusCounts),
    datasets: [
      {
        label: 'Permit Count',
        data: Object.values(statusCounts),
        backgroundColor: ['rgba(0, 168, 132, 0.8)', 'rgba(0, 121, 107, 0.8)', 'rgba(178, 223, 219, 0.8)', 'rgba(255, 193, 7, 0.8)'],
      },
    ],
  };

  const agencyChartData = {
    labels: Object.keys(agencyCounts),
    datasets: [
      {
        label: 'Permits by Agency',
        data: Object.values(agencyCounts),
        backgroundColor: ['rgba(0, 105, 92, 0.8)', 'rgba(0, 77, 64, 0.8)', 'rgba(38, 166, 154, 0.8)', 'rgba(128, 203, 196, 0.8)', 'rgba(0, 77, 64, 0.8)'],
      },
    ],
  };

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
        backdropFilter: 'blur(15px)',
        border: '1px solid',
        borderColor: 'success.200',
        boxShadow: '0 4px 20px rgba(0, 168, 132, 0.08)',
        borderRadius: 'xl',
        overflow: 'hidden',
        p: 0
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100' }}>
        <Typography 
          level="h3" 
          sx={{ 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            fontWeight: 700,
            mb: 1
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '28px', fontFamily: 'Material Symbols Outlined' }}>
            analytics
          </span>
          Project Analytics
        </Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          Overview of your permit status and agency distribution
        </Typography>
      </Box>

      <Box sx={{ p: 4 }}>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #00A884, #007E33)',
                color: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0, 168, 132, 0.25)',
                }
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', mx: 'auto', mb: 1, width: 48, height: 48 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                  assignment
                </span>
              </Avatar>
              <Typography level="h2" sx={{ fontWeight: 700, color: 'white' }}>{permits.length}</Typography>
              <Typography level="body-sm" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Total Permits</Typography>
            </Card>
          </Grid>

          <Grid xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #dc3545, #c82333)',
                color: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(220, 53, 69, 0.25)',
                }
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', mx: 'auto', mb: 1, width: 48, height: 48 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                  schedule
                </span>
              </Avatar>
              <Typography level="h2" sx={{ fontWeight: 700, color: 'white' }}>
                {permits.filter(p => {
                  const exp = new Date(p.expiration_date);
                  const now = new Date();
                  const in30Days = new Date(now);
                  in30Days.setDate(now.getDate() + 30);
                  return p.expiration_date && exp >= now && exp <= in30Days;
                }).length}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Expiring Soon</Typography>
            </Card>
          </Grid>

          <Grid xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #28a745, #218838)',
                color: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(40, 167, 69, 0.25)',
                }
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', mx: 'auto', mb: 1, width: 48, height: 48 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                  check_circle
                </span>
              </Avatar>
              <Typography level="h2" sx={{ fontWeight: 700, color: 'white' }}>{statusCounts['Active']}</Typography>
              <Typography level="body-sm" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Active Permits</Typography>
            </Card>
          </Grid>

          <Grid xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #17a2b8, #138496)',
                color: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(23, 162, 184, 0.25)',
                }
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', mx: 'auto', mb: 1, width: 48, height: 48 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                  verified
                </span>
              </Avatar>
              <Typography level="h2" sx={{ fontWeight: 700, color: 'white' }}>{statusCounts['Certified']}</Typography>
              <Typography level="body-sm" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Certified</Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={4}>
          <Grid xs={12} lg={8}>
            <Card sx={{ p: 3, height: '100%', borderRadius: 'lg' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'success.100', color: 'success.600' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    pie_chart
                  </span>
                </Avatar>
                <Box>
                  <Typography level="h4" sx={{ color: 'success.700', fontWeight: 600 }}>Records by Status</Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Distribution of permit statuses</Typography>
                </Box>
              </Box>
              <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Pie
                  data={statusChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          padding: 20,
                          usePointStyle: true,
                        }
                      },
                      datalabels: {
                        color: '#fff',
                        font: {
                          weight: 'bold',
                          size: 12
                        },
                        formatter: (value, context) => {
                          if (value === 0) return '';
                          return value;
                        },
                      },
                    },
                  }}
                />
              </Box>
            </Card>
          </Grid>

          <Grid xs={12} lg={4}>
            <Card sx={{ p: 3, height: '100%', borderRadius: 'lg' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.100', color: 'primary.600' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    bar_chart
                  </span>
                </Avatar>
                <Box>
                  <Typography level="h4" sx={{ color: 'success.700', fontWeight: 600 }}>By Agency</Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Agency distribution</Typography>
                </Box>
              </Box>
              <Box sx={{ height: 250 }}>
                <Bar
                  data={agencyChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'y',
                    plugins: {
                      legend: {
                        display: false
                      },
                      datalabels: {
                        anchor: 'end',
                        align: 'right',
                        color: '#333',
                        font: {
                          weight: 'bold',
                          size: 11
                        },
                        formatter: (value) => value > 0 ? value : '',
                      },
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                        grid: {
                          display: true,
                          color: 'rgba(0, 0, 0, 0.1)',
                          lineWidth: 1,
                        },
                      },
                      y: {
                        grid: {
                          display: false,
                        },
                      },
                    },
                  }}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Status Legend */}
        <Card sx={{ p: 3, mt: 4, borderRadius: 'lg', bgcolor: 'neutral.50' }}>
          <Typography level="title-sm" sx={{ mb: 2, color: 'success.700', fontWeight: 600 }}>Status Definitions</Typography>
          <Grid container spacing={3}>
            <Grid xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip size="sm" variant="soft" sx={{ bgcolor: `${getStatusColor('In Progress')}20`, color: getStatusColor('In Progress') }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                    {getStatusIcon('In Progress')}
                  </span>
                </Chip>
                <Box>
                  <Typography level="body-sm" sx={{ fontWeight: 500 }}>In Progress</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Has a start date only</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip size="sm" variant="soft" sx={{ bgcolor: `${getStatusColor('In Review')}20`, color: getStatusColor('In Review') }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                    {getStatusIcon('In Review')}
                  </span>
                </Chip>
                <Box>
                  <Typography level="body-sm" sx={{ fontWeight: 500 }}>In Review</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Application submitted</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip size="sm" variant="soft" sx={{ bgcolor: `${getStatusColor('Active')}20`, color: getStatusColor('Active') }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                    {getStatusIcon('Active')}
                  </span>
                </Chip>
                <Box>
                  <Typography level="body-sm" sx={{ fontWeight: 500 }}>Active</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Has expiration date</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip size="sm" variant="soft" sx={{ bgcolor: `${getStatusColor('Certified')}20`, color: getStatusColor('Certified') }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                    {getStatusIcon('Certified')}
                  </span>
                </Chip>
                <Box>
                  <Typography level="body-sm" sx={{ fontWeight: 500 }}>Certified</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Certified by Engineer</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Box>
    </Card>
  );
};

export default ClientAnalytics;
